// components/referral/ReferralStatus.jsx
import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

const ReferralStatus = ({ user }) => {
  const [referralStatus, setReferralStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [claimData, setClaimData] = useState({
    referralCode: ''
  });
  const [validationState, setValidationState] = useState({
    validating: false,
    valid: false,
    referrerInfo: null,
    error: null
  });

  const functions = getFunctions();

  useEffect(() => {
    if (user) {
      loadReferralStatus();
    }
  }, [user]);

  const loadReferralStatus = async () => {
    try {
      setLoading(true);
      const getReferralStatus = httpsCallable(functions, 'getReferralStatus');
      const result = await getReferralStatus();

      if (result.data.success) {
        setReferralStatus(result.data.referralStatus);
      }
    } catch (error) {
      console.error('Error loading referral status:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateReferralCode = async (code) => {
    if (!code.trim()) {
      setValidationState({ validating: false, valid: false, referrerInfo: null, error: null });
      return;
    }

    setValidationState({ ...validationState, validating: true, error: null });

    try {
      const validateReferralCode = httpsCallable(functions, 'validateReferralCode');
      const result = await validateReferralCode({ referralCode: code });

      if (result.data.valid) {
        setValidationState({
          validating: false,
          valid: true,
          referrerInfo: result.data.referrer,
          error: null
        });
      } else {
        setValidationState({
          validating: false,
          valid: false,
          referrerInfo: null,
          error: result.data.error || 'Invalid referral code'
        });
      }
    } catch (error) {
      console.error('Error validating referral code:', error);
      setValidationState({
        validating: false,
        valid: false,
        referrerInfo: null,
        error: 'Failed to validate referral code'
      });
    }
  };

  const claimReferral = async () => {
    if (!validationState.valid) return;

    try {
      setClaiming(true);
      const claimReferral = httpsCallable(functions, 'claimReferral');
      
      const result = await claimReferral({
        referralCode: claimData.referralCode
      });

      if (result.data.success) {
        await loadReferralStatus(); // Refresh status
        setClaimData({ referralCode: '' });
        setValidationState({ validating: false, valid: false, referrerInfo: null, error: null });
      } else {
        setValidationState(prev => ({ ...prev, error: result.data.error }));
      }
    } catch (error) {
      console.error('Error claiming referral:', error);
      setValidationState(prev => ({ ...prev, error: 'Failed to claim referral' }));
    } finally {
      setClaiming(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount || 0);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp.seconds * 1000).toLocaleDateString();
  };

  if (loading) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        backgroundColor: '#1F2937',
        borderRadius: '20px',
        border: '4px solid #374151'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid #374151',
          borderTop: '4px solid #10B981',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 20px'
        }}></div>
        <p style={{ color: '#9CA3AF', margin: 0 }}>Loading referral status...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div style={{ padding: '0' }}>
      <div style={{
        backgroundColor: '#1F2937',
        borderRadius: '20px',
        padding: '32px',
        border: '4px solid #374151'
      }}>
        <h2 style={{
          fontSize: '28px',
          fontWeight: 'bold',
          margin: '0 0 8px 0',
          color: '#8B5CF6',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span>🤝</span>
          Referral Status
        </h2>

        {referralStatus?.hasReferral ? (
          <div>
            <p style={{
              color: '#9CA3AF',
              fontSize: '16px',
              margin: '0 0 24px 0',
              lineHeight: '1.5'
            }}>
              A musician shared their code with you and they're earning from your success!
            </p>

            <div style={{
              backgroundColor: '#065F46',
              borderRadius: '16px',
              padding: '24px',
              border: '3px solid #10B981',
              marginBottom: '24px'
            }}>
              <h3 style={{
                color: '#6EE7B7',
                fontSize: '20px',
                fontWeight: 'bold',
                margin: '0 0 16px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>✅</span>
                The Musician Who Introduced You
              </h3>
              
              <div style={{
                backgroundColor: '#064E3B',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '16px',
                border: '2px solid #059669'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <div>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      color: '#6EE7B7',
                      marginBottom: '4px'
                    }}>
                      {referralStatus.referrer?.name}
                    </div>
                    <div style={{
                      color: '#A7F3D0',
                      fontSize: '14px',
                      fontFamily: 'monospace'
                    }}>
                      Code: {referralStatus.referrer?.code}
                    </div>
                  </div>
                  <div style={{
                    backgroundColor: referralStatus.status === 'active' ? '#10B981' : '#6B7280',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    textTransform: 'uppercase'
                  }}>
                    {referralStatus.status}
                  </div>
                </div>

                {referralStatus.status === 'active' && (
                  <div style={{
                    color: '#A7F3D0',
                    fontSize: '14px',
                    lineHeight: '1.4'
                  }}>
                    💰 They've earned <strong>{formatCurrency(referralStatus.totalCommissions)}</strong> from your performances so far!
                  </div>
                )}
              </div>

              <div style={{
                color: '#A7F3D0',
                fontSize: '14px',
                lineHeight: '1.4'
              }}>
                📅 <strong>Joined through their code on:</strong> {formatDate(referralStatus.createdAt)}<br />
                {referralStatus.status === 'pending' && (
                  <>🎯 <strong>Status:</strong> Waiting for your first paid performance to activate</>
                )}
                {referralStatus.status === 'active' && (
                  <>⏰ <strong>Commission Period:</strong> Active for 12 months from first payment</>
                )}
              </div>
            </div>
          </div>
        ) : referralStatus?.canClaimReferral ? (
          <div>
            <p style={{
              color: '#9CA3AF',
              fontSize: '16px',
              margin: '0 0 24px 0',
              lineHeight: '1.5'
            }}>
              You have {referralStatus.gracePeriodDaysLeft} days left to claim a referral. After that, this option expires.
            </p>

            <div style={{
              backgroundColor: '#7C2D12',
              borderRadius: '16px',
              padding: '24px',
              border: '3px solid #EA580C',
              marginBottom: '24px'
            }}>
              <h3 style={{
                color: '#FDBA74',
                fontSize: '20px',
                fontWeight: 'bold',
                margin: '0 0 8px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>⏰</span>
                Credit A Musician
              </h3>
              <p style={{
                color: '#FED7AA',
                fontSize: '14px',
                margin: '0 0 20px 0',
                lineHeight: '1.5'
              }}>
                If another musician shared RequestASong with you, enter their code now to give them credit!
              </p>

              <div style={{ marginBottom: '20px' }}>
                <input
                  type="text"
                  placeholder="Enter the code they shared with you"
                  value={claimData.referralCode}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setClaimData(prev => ({ ...prev, referralCode: value }));
                    if (value.length >= 3) {
                      validateReferralCode(value);
                    } else {
                      setValidationState({ validating: false, valid: false, referrerInfo: null, error: null });
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '16px',
                    fontSize: '16px',
                    border: '2px solid #92400E',
                    borderRadius: '8px',
                    backgroundColor: '#374151',
                    color: 'white',
                    outline: 'none'
                  }}
                />

                {/* Validation Feedback */}
                {validationState.validating && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: '#1E40AF',
                    borderRadius: '8px',
                    color: '#BFDBFE',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #BFDBFE',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Validating...
                  </div>
                )}

                {validationState.valid && validationState.referrerInfo && (
                  <div style={{
                    marginTop: '12px',
                    padding: '16px',
                    backgroundColor: '#064E3B',
                    borderRadius: '12px',
                    border: '2px solid #10B981',
                    color: '#6EE7B7',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <span style={{ fontSize: '20px' }}>✅</span>
                    <div>
                      <strong>Musician found!</strong><br />
                      {validationState.referrerInfo.name} will be credited for sharing their code with you.
                    </div>
                  </div>
                )}

                {validationState.error && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: '#7F1D1D',
                    borderRadius: '8px',
                    color: '#FCA5A5',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span>❌</span>
                    {validationState.error}
                  </div>
                )}
              </div>

              <button
                onClick={claimReferral}
                disabled={!validationState.valid || claiming}
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: validationState.valid && !claiming ? '#10B981' : '#6B7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: validationState.valid && !claiming ? 'pointer' : 'not-allowed',
                  transition: 'background-color 0.3s ease'
                }}
              >
                {claiming ? '⏳ Processing...' : '🤝 Credit This Musician'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{
            backgroundColor: '#374151',
            borderRadius: '16px',
            padding: '24px',
            border: '3px solid #4B5563',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚀</div>
            <h3 style={{
              color: '#C4B5FD',
              fontSize: '20px',
              fontWeight: 'bold',
              margin: '0 0 12px 0'
            }}>
              No Referral Link
            </h3>
            <p style={{
              color: '#9CA3AF',
              fontSize: '16px',
              margin: '0',
              lineHeight: '1.5'
            }}>
              You joined RequestASong independently. That's awesome! You can still earn by referring other musicians through your own referral code.
            </p>
          </div>
        )}

        {/* Information Section */}
        <div style={{
          backgroundColor: '#1E3A8A',
          borderRadius: '16px',
          padding: '24px',
          border: '3px solid #3B82F6',
          marginTop: '24px'
        }}>
          <h3 style={{
            color: '#93C5FD',
            fontSize: '20px',
            fontWeight: 'bold',
            margin: '0 0 16px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>💡</span>
            About Referrals
          </h3>
          
          <div style={{
            color: '#BFDBFE',
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            <div style={{ marginBottom: '12px' }}>
              • When someone refers you, they earn 2% commission from your earnings for 12 months
            </div>
            <div style={{ marginBottom: '12px' }}>
              • This doesn't reduce your earnings - it's a bonus paid by the platform
            </div>
            <div style={{ marginBottom: '12px' }}>
              • Referrals help musicians discover the platform and grow the community
            </div>
            <div>
              • You can create your own referral code to start earning from musicians you refer!
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralStatus;